import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase, supabaseReal } from './lib/supabase/client';
import Login from './pages/Login';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import RechnungenListe from './pages/admin/rechnungen/RechnungenListe';
import EigentuemerListe from './pages/admin/eigentuemer/EigentuemerListe';
import MieterListe from './pages/admin/mieter/MieterListe';
import KontaktUmleitung from './pages/admin/kontakte/KontaktUmleitung';
import SonstigeListeSeite from './pages/admin/kontakte/sonstige/SonstigeListeSeite';
import SonstigeDetailSeite from './pages/admin/kontakte/sonstige/SonstigeDetailSeite';
import DienstleisterBereich from './pages/admin/dienstleister/DienstleisterBereich';
import AlteEinheitWeiterleitung from './pages/admin/objekte/AlteEinheitWeiterleitung';
import ObjekteListe from './pages/admin/objekte/ObjekteListe';
import { istOeffentlicheSeite } from './lib/auth/oeffentlicheSeiten';
import DokumenteSeite from './pages/admin/dokumente/DokumenteSeite';
import TicketListe from './pages/admin/tickets/TicketListe';
import KalenderView from './pages/admin/kalender/KalenderView';
import VertraegeSeite from './pages/admin/vertraege/VertraegeSeite';
import SelbstauskunftListe from './pages/admin/selbstauskunft/SelbstauskunftListe';
import { SerienmailWeiterleitung } from './pages/admin/emails/bereich/SerienmailWeiterleitung';
import UebergabeListe from './pages/admin/uebergabe/UebergabeListe';
import ZeiterfassungSeite from './pages/admin/zeiterfassung/ZeiterfassungSeite';
import AbwesenheitenSeite from './pages/admin/zeiterfassung/AbwesenheitenSeite';
import MonatsabschlussSeite from './pages/admin/zeiterfassung/MonatsabschlussSeite';
import PersonalakteSeite from './pages/admin/personal/PersonalakteSeite';
import HausmeisterApp from './pages/hausmeister/HausmeisterApp';
import ZugangHinweis from './components/portal/ZugangHinweis';
import PortalWeiche from './components/portal/PortalWeiche';
import { zugangFuer } from './lib/portal/zugang';
import ZaehlerBereich from './pages/admin/zaehler/ZaehlerBereich';
import { EmailBereich } from './pages/admin/emails/bereich/EmailBereich';
import MailFenster from './pages/admin/emails/MailFenster';
import TeamListe from './pages/admin/team/TeamListe';
import MitarbeiterDetailSeite from './pages/admin/team/MitarbeiterDetailSeite';
import ProfileView from './pages/admin/profile/ProfileView';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import SchreibenAssistent from './pages/admin/schreiben/SchreibenAssistent';
import SchreibenUebersicht from './pages/admin/schreiben/SchreibenUebersicht';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TerminAntwortSeite from './pages/public/TerminAntwortSeite';
import BuchungsSeite from './pages/public/BuchungsSeite';
import SelbstauskunftSeite from './pages/public/SelbstauskunftSeite';
import SignierenSeite from './pages/public/SignierenSeite';
import { AlertCircle, LogOut, Loader2, UserPlus } from 'lucide-react';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { ZeitProvider } from './contexts/ZeitContext';
import { TicketTimerProvider } from './contexts/TicketTimerContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { SidebarCollapseProvider } from './contexts/SidebarCollapseContext';

export type ViewType = 'dashboard' | 'rechnungen' | 'eigentuemer' | 'mieter' | 'objekte' | 'dokumente' | 'team' | 'profile' | 'super-admin' | string;

const RESUME_KEY = 'immo_last_path';

// Passwort-Reset (design_handoff_login §2): Der Link aus der Reset-Mail kommt mit #…type=recovery zurück.
// Supabase speichert dabei eine Session und meldet PASSWORD_RECOVERY erst verzögert – deshalb die Adresse schon
// beim Laden des Moduls prüfen (bevor Supabase sie bereinigt), damit nicht kurz das Dashboard erscheint.
const RECOVERY_IN_URL = typeof window !== 'undefined' && /type=recovery/.test(window.location.hash);

const readResumePath = (): string => {
  try {
    const saved = localStorage.getItem(RESUME_KEY);
    return saved && saved !== '/' && saved !== '/admin' ? saved : '/admin/dashboard';
  } catch {
    return '/admin/dashboard';
  }
};

// Login-Adresse: Ohne Sitzung landet jede Seite auf /login?weiter=<ursprünglicher Pfad>,
// nach der Anmeldung geht es dorthin zurück. Nur interne Pfade zulassen (kein //host, kein http:).
const LOGIN_PFAD = '/login';
const sichererWeiterPfad = (roh: string | null): string => {
  if (!roh || !roh.startsWith('/') || roh.startsWith('//') || roh.startsWith(LOGIN_PFAD)) return '/';
  return roh;
};

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const isInitialized = useRef(false);
  // Solange true: Login im Modus „Neues Passwort“ statt der App (Recovery-Link geöffnet).
  const [recovery, setRecovery] = useState(RECOVERY_IN_URL);

  const navigate = useNavigate();
  const location = useLocation();

  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [pendingMieterId, setPendingMieterId] = useState<string | null>(null);
  const [pendingEigentuemerId, setPendingEigentuemerId] = useState<string | null>(null);
  const [pendingTerminId, setPendingTerminId] = useState<string | null>(null);
  const [pendingEmailId, setPendingEmailId] = useState<string | null>(null);


  const moduleOrder = ['dashboard', 'objekte', 'mieter', 'eigentuemer', 'rechnungen', 'dokumente', 'tickets', 'kalender', 'vertraege', 'zaehler', 'emails', 'team', 'profile'];

  // Aktuellen Admin-Pfad fuer den Resume-Redirect merken (ersetzt das alte handleNavigate-localStorage).
  useEffect(() => {
    if (location.pathname.startsWith('/admin/')) {
      try { localStorage.setItem(RESUME_KEY, location.pathname); } catch { /* ignore */ }
    }
  }, [location.pathname]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        initializeUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        setSession(session);
        if (session) initializeUserData(session.user.id);
      } else if (event === 'PASSWORD_RECOVERY') {
        setSession(session);
        setRecovery(true);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUserProfile(null);
        setActiveModules([]);
        isInitialized.current = false;
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const ensureArray = (data: any): string[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return data.replace(/[{}]/g, '').split(',').filter(Boolean);
      }
    }
    return [];
  };

  const initializeUserData = async (userId: string) => {
    setInitError(null);
    try {
      const { data: profile, error: profileError } = await supabaseReal
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile) {
        setInitError('missing_profile');
        setLoading(false);
        return;
      }

      // Team-Mitgliedschaft laden: IDs aller Kollegen, die mind. ein Team mit dem User teilen,
      // sowie die Team-IDs des Users selbst (für team-zugewiesene Tickets).
      let teamMemberIds: string[] = [profile.id];
      let meineTeamIds: string[] = [];
      try {
        const { data: myTeams } = await supabaseReal
          .from('team_members')
          .select('team_id')
          .eq('profile_id', userId);
        meineTeamIds = (myTeams || []).map((t: any) => t.team_id);
        if (meineTeamIds.length > 0) {
          const { data: colleagues } = await supabaseReal
            .from('team_members')
            .select('profile_id')
            .in('team_id', meineTeamIds);
          teamMemberIds = Array.from(
            new Set([profile.id, ...((colleagues || []).map((c: any) => c.profile_id))])
          );
        }
      } catch (e) {
        console.warn('Team-Mitglieder konnten nicht geladen werden:', e);
      }

      setUserProfile({ ...profile, team_member_ids: teamMemberIds, team_ids: meineTeamIds });

      const { data: modules, error: modulesError } = await supabase
        .from('portal_modules')
        .select('id')
        .eq('is_active', true);

      const activeIds = (modulesError || !modules) ? moduleOrder : [...modules.map(m => m.id), 'profile'];
      setActiveModules(activeIds);

      // Dynamische Landing-Page Logik fuer Team-Mitglieder
      if (profile.role === 'team') {
        const allowed = ensureArray(profile.allowed_modules);
        // #555: Das Mailfenster (/mail-fenster/:id) gehört zum Modul „emails“ — sonst galt die
        // Mail-ID als Bereich, und Team-Mitglieder landeten auf dem Dashboard.
        // Frische Adresse lesen (location ist hier ggf. veraltet); auf /login zählt das Ziel aus ?weiter=.
        const aktuellerPfad = window.location.pathname === LOGIN_PFAD
          ? sichererWeiterPfad(new URLSearchParams(window.location.search).get('weiter')).split('?')[0]
          : window.location.pathname;
        // #586: Der Schreiben-Assistent (/admin/schreiben/…) gehört zum Modul „dokumente“
        const currentView = aktuellerPfad.startsWith('/mail-fenster/')
          ? 'emails'
          : aktuellerPfad.startsWith('/admin/schreiben')
            ? 'dokumente'
            : aktuellerPfad.split('/').filter(Boolean)[1] || 'dashboard';

        // Wenn die aktuelle Ansicht (oder Default Dashboard) nicht erlaubt ist, weiterleiten –
        // #613: nie von öffentlichen Seiten weg (Signier-Link aus der Mail öffnete sonst die Startseite)
        if (!istOeffentlicheSeite(aktuellerPfad) && currentView !== 'profile' && !allowed.includes(currentView)) {
          const firstAllowed = moduleOrder.find(m => allowed.includes(m) && activeIds.includes(m));
          if (firstAllowed) {
            navigate(`/admin/${firstAllowed}`, { replace: true });
          } else {
            navigate('/admin/profile', { replace: true });
          }
        }
      }

      isInitialized.current = true;
    } catch (err: any) {
      console.error('Error initializing app data:', err);
      setInitError(err.message || 'Systemfehler bei der Initialisierung');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchNavigation = (path: string, id: string, type: string) => {
    if (type === 'objekt') { navigate('/admin/objekte/' + id); return; }
    // Kontakt-Treffer aus der ⌘K-Suche tragen eine präfixierte id ("mieter:uuid") —
    // die rohe UUID für die Route herauslösen (sonst leere Detailseite).
    if (type === 'mieter') { const rohId = id.includes(':') ? id.split(':').pop()! : id; navigate('/admin/mieter/' + rohId); return; }
    if (type === 'rechnung' || type === 'rechnungen') { navigate('/admin/rechnungen/' + id); return; }
    if (type === 'eigentuemer') { const rohId = id.includes(':') ? id.split(':').pop()! : id; navigate('/admin/eigentuemer/' + rohId); return; }
    if (type === 'ticket' || type === 'tickets') { navigate('/admin/tickets/' + id); return; }
    if (type === 'email') { setPendingEmailId(id); navigate('/admin/emails'); return; }
    if (type === 'termin') { setPendingTerminId(id); navigate('/admin/kalender'); return; }
    if (type === 'vertrag' || type === 'vertraege') { navigate('/admin/vertraege/' + id); return; }
    if (type === 'zaehler') { navigate('/admin/zaehler/' + id); return; }
    navigate(path);
  };

  // Öffentliche Antwort-Seite für Termin-Einladungen — ohne Login erreichbar,
  // MUSS vor Loading-/Session-Checks stehen (sonst sehen Gäste die Login-Maske).
  if (location.pathname === '/termin-antwort') {
    return <TerminAntwortSeite />;
  }

  // Öffentliche Buchungsseite (/buchen/:slug) — ohne Login erreichbar, ebenfalls
  // MUSS vor Loading-/Session-Checks stehen.
  if (location.pathname.startsWith('/buchen/')) {
    return <BuchungsSeite />;
  }

  // Öffentliche Mieterselbstauskunft (/selbstauskunft/:slug) — ohne Login erreichbar,
  // ebenfalls MUSS vor Loading-/Session-Checks stehen.
  if (location.pathname.startsWith('/selbstauskunft/')) {
    return <SelbstauskunftSeite />;
  }

  // Öffentliche Signier-Seite (/signieren/:token) — ohne Login erreichbar,
  // ebenfalls MUSS vor Loading-/Session-Checks stehen.
  if (location.pathname.startsWith('/signieren/')) {
    return <SignierenSeite />;
  }

  // Recovery-Link: neues Passwort setzen, danach meldet Login ab und gibt die Anmeldemaske frei.
  if (recovery) {
    return <Login initialMode="newpw" onRecoveryEnde={() => setRecovery(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf8]">
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 border-[3px] border-[#c4a77d]/20 border-t-[#c4a77d] rounded-full animate-spin"></div>
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.4em] animate-pulse">System-Check...</p>
        </div>
      </div>
    );
  }

  if (initError === 'missing_profile' && session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf8] p-6">
        <div className="max-w-md w-full bg-white rounded-[3.5rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] p-12 text-center border border-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-[#c4a77d]"></div>
          <div className="w-20 h-20 bg-[#fdfbf8] text-[#c4a77d] rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-[#f5eee2]">
            <UserPlus size={40} />
          </div>
          <h2 className="text-xl font-black text-[#0f172a] uppercase tracking-tight mb-4">Profil nicht gefunden</h2>
          <p className="text-[12px] font-semibold text-gray-400 leading-relaxed uppercase tracking-wider mb-10">
            Ihr Account wurde erstellt, aber das Profil ist noch nicht vollständig synchronisiert.
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="w-full py-5 bg-[#0f172a] text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-4 hover:bg-black transition-all shadow-xl active:scale-95"
          >
            <LogOut size={18} />
            Zurück zum Haupt-Login
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    if (location.pathname !== LOGIN_PFAD) {
      const ziel = location.pathname + location.search;
      const weiter = ziel !== '/' ? '?weiter=' + encodeURIComponent(ziel) : '';
      return <Navigate to={LOGIN_PFAD + weiter} replace />;
    }
    return <Login />;
  }

  // Angemeldet, aber noch auf /login: zurück zur ursprünglichen Seite (oder Startseite).
  if (location.pathname === LOGIN_PFAD) {
    return <Navigate to={sichererWeiterPfad(new URLSearchParams(location.search).get('weiter'))} replace />;
  }

  // Rollen-Weiche (E0): gesperrte Konten, Portal-Rollen (Mieter/Eigentümer) und
  // unbekannte Rollen sehen den Admin-Bereich nicht. Die Datenbank schützt zusätzlich.
  const zugang = userProfile ? zugangFuer(userProfile) : 'verwaltung';
  if (zugang === 'gesperrt' || zugang === 'kein_zugang') {
    return <ZugangHinweis art={zugang} />;
  }
  // Mieter/Eigentümer: eigenes Portal (nur wenn eingeschaltet), nie der Admin-Bereich
  if (zugang === 'portal') {
    return <PortalWeiche profil={userProfile} />;
  }

  // Hausmeister sehen die Verwaltungsoberfläche gar nicht — sie bekommen ihre
  // eigene, schmale Ansicht. Die Datenbank schützt zusätzlich (RLS), das hier ist
  // die Bequemlichkeitsebene.
  if (zugang === 'hausmeister') {
    return (
      <ZeitProvider userId={session?.user?.id || null}>
        <HausmeisterApp userProfile={userProfile} />
      </ZeitProvider>
    );
  }

  return (
    <NotificationProvider userId={session?.user?.id || null}>
      <ChatProvider userId={session?.user?.id || null}>
      <ZeitProvider userId={session?.user?.id || null}>
      <TicketTimerProvider userId={session?.user?.id || null}>
      <DemoModeProvider userRole={userProfile?.role}>
        <SidebarCollapseProvider>
          <ProtectedRoute session={session}>
              <Routes>
                <Route
                  path="/admin"
                  element={<AdminLayout userProfile={userProfile} onSearchNavigate={handleSearchNavigation} />}
                >
                  <Route index element={<Navigate to={readResumePath()} replace />} />
                  <Route path="dashboard" element={<Dashboard userProfile={userProfile} onNavigate={navigate} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToTicket={(id) => navigate('/admin/tickets/' + id)} onNavigateToEmail={(id) => { setPendingEmailId(id); navigate('/admin/emails'); }} />} />
                  <Route path="rechnungen" element={<RechnungenListe userProfile={userProfile} onNavigateToObjekt={(id) => handleSearchNavigation('/admin/objekte', id, 'objekt')} />} />
                  <Route path="rechnungen/:id" element={<RechnungenListe userProfile={userProfile} onNavigateToObjekt={(id) => handleSearchNavigation('/admin/objekte', id, 'objekt')} />} />
                  <Route path="dokumente" element={<DokumenteSeite userProfile={userProfile} />} />
                  <Route path="schreiben" element={<SchreibenUebersicht />} />
                  <Route path="schreiben/neu" element={<SchreibenAssistent />} />
                  <Route path="dokumente/:id" element={<DokumenteSeite userProfile={userProfile} />} />
                  <Route path="kontakte" element={<SonstigeListeSeite />} />
                  <Route path="kontakte/kontakt/:id" element={<SonstigeDetailSeite />} />
                  <Route path="kontakte/:quelle/:id" element={<KontaktUmleitung />} />
                  <Route path="dienstleister" element={<DienstleisterBereich />} />
                  <Route path="dienstleister/:dienstleisterId" element={<DienstleisterBereich />} />
                  <Route path="eigentuemer" element={<EigentuemerListe initialEigentuemerId={pendingEigentuemerId} onClearInitial={() => setPendingEigentuemerId(null)} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} />} />
                  <Route path="eigentuemer/:eigentuemerId" element={<EigentuemerListe initialEigentuemerId={pendingEigentuemerId} onClearInitial={() => setPendingEigentuemerId(null)} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} />} />
                  <Route path="mieter" element={<MieterListe initialMieterId={pendingMieterId} onClearInitial={() => setPendingMieterId(null)} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} />} />
                  <Route path="mieter/:mieterId" element={<MieterListe initialMieterId={pendingMieterId} onClearInitial={() => setPendingMieterId(null)} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} />} />
                  <Route path="tickets" element={<TicketListe userProfile={userProfile} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToTermin={(id) => { setPendingTerminId(id); navigate('/admin/kalender'); }} />} />
                  <Route path="tickets/:id" element={<TicketListe userProfile={userProfile} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToTermin={(id) => { setPendingTerminId(id); navigate('/admin/kalender'); }} />} />
                  <Route path="kalender" element={<KalenderView userProfile={userProfile} initialTerminId={pendingTerminId} onClearInitial={() => setPendingTerminId(null)} onNavigateToObjekt={(id) => navigate('/admin/objekte/' + id)} onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToEigentuemer={(id) => navigate('/admin/eigentuemer/' + id)} onNavigateToTicket={(id) => navigate('/admin/tickets/' + id)} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToEmail={(id) => { setPendingEmailId(id); navigate('/admin/emails'); }} />} />
                  <Route path="vertraege" element={<VertraegeSeite />} />
                  <Route path="vertraege/:id" element={<VertraegeSeite />} />
                  <Route path="mieterselbstauskunft" element={<SelbstauskunftListe />} />
                  <Route path="serienmail" element={<SerienmailWeiterleitung />} />
                  <Route path="serienmail/neu" element={<SerienmailWeiterleitung />} />
                  <Route path="serienmail/:id" element={<SerienmailWeiterleitung />} />
                  <Route path="zeiterfassung" element={<ZeiterfassungSeite userProfile={userProfile} />} />
                  <Route path="abwesenheiten" element={<AbwesenheitenSeite userProfile={userProfile} />} />
                  <Route path="monatsabschluss" element={<MonatsabschlussSeite userProfile={userProfile} />} />
                  <Route path="personalakte" element={<PersonalakteSeite userProfile={userProfile} />} />
                  <Route path="uebergabeprotokolle" element={<UebergabeListe userProfile={userProfile} />} />
                  <Route path="uebergabeprotokolle/:id" element={<UebergabeListe userProfile={userProfile} />} />
                  <Route path="zaehler" element={<ZaehlerBereich userProfile={userProfile} />} />
                  <Route path="zaehler/:id" element={<ZaehlerBereich userProfile={userProfile} />} />
                  <Route path="emails" element={<EmailBereich userProfile={userProfile} initialEmailId={pendingEmailId} onClearInitial={() => setPendingEmailId(null)} />} />
                  <Route path="team" element={<TeamListe />} />
                  <Route path="team/:mitarbeiterId" element={<MitarbeiterDetailSeite />} />
                  <Route path="objekte" element={<ObjekteListe onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToEigentuemer={(id) => navigate('/admin/eigentuemer/' + id)} onNavigateToDokumente={(objektId?: string) => navigate(objektId ? '/admin/dokumente?objekt=' + objektId : '/admin/dokumente')} />} />
                  <Route path="objekte/:id" element={<ObjekteListe onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToEigentuemer={(id) => navigate('/admin/eigentuemer/' + id)} onNavigateToDokumente={(objektId?: string) => navigate(objektId ? '/admin/dokumente?objekt=' + objektId : '/admin/dokumente')} />} />
                  <Route path="objekte/:id/einheiten" element={<ObjekteListe onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToEigentuemer={(id) => navigate('/admin/eigentuemer/' + id)} onNavigateToDokumente={(objektId?: string) => navigate(objektId ? '/admin/dokumente?objekt=' + objektId : '/admin/dokumente')} />} />
                  <Route path="objekte/:id/einheiten/:einheitId" element={<ObjekteListe onNavigateToMieter={(id) => navigate('/admin/mieter/' + id)} onNavigateToRechnung={(id) => navigate('/admin/rechnungen/' + id)} onNavigateToEigentuemer={(id) => navigate('/admin/eigentuemer/' + id)} onNavigateToDokumente={(objektId?: string) => navigate(objektId ? '/admin/dokumente?objekt=' + objektId : '/admin/dokumente')} />} />
                  {/* Alter Pfad (vor dem Redesign) – Links aus Mails/Lesezeichen bleiben gültig */}
                  <Route path="objekte/:id/einheit/:einheitId" element={<AlteEinheitWeiterleitung />} />
                  <Route path="profile" element={<ProfileView userProfile={userProfile} onRefresh={() => initializeUserData(session.user.id)} />} />
                  <Route path="super-admin" element={userProfile?.role === 'super admin' ? <SuperAdminDashboard /> : <Navigate to="/admin/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Route>
                {/* #551: eine E-Mail im eigenen Browserfenster (ohne Admin-Rahmen) */}
                <Route path="/mail-fenster/:id" element={<MailFenster userProfile={userProfile} />} />
                <Route path="/" element={<Navigate to={readResumePath()} replace />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
          </ProtectedRoute>
        </SidebarCollapseProvider>
      </DemoModeProvider>
      </TicketTimerProvider>
      </ZeitProvider>
      </ChatProvider>
    </NotificationProvider>
  );
};

export default App;
